import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  FormProps,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Upload,
} from "antd";
import { FC, useEffect, useState } from "react";
import { StarRating } from "~/components/common/star-rating";
import { useGetStaffRoles } from "~/services/common";
import {
  CreateStaffParams,
  OPERATION_KEYS,
  StaffRoleAssignmentInput,
  useCreateStaff,
  useGetStaffById,
  useUpdateStaff,
} from "~/services/operation";
import { useGetFiles, useCreateFile, useDeleteFile, useUpdateFile } from "~/services/file";
import { TFile } from "~/types";
import { FILE_KEYS } from "~/services/file/file.keys";
import { capitalize } from "~/utils";
import { DocumentType, EmploymentType, Role, STAFF_DOCUMENT_TYPES, StaffStatus } from "~/utils/constants";
import { useStaffStore } from "../staffs.store";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

// Labels for staff status dropdown (excluding ON_ASSIGNMENT which is deprecated)
const staffStatusOptions: { value: StaffStatus; label: string }[] = [
  { value: StaffStatus.ACTIVE, label: "Active" },
  { value: StaffStatus.HIATUS, label: "Hiatus" },
  { value: StaffStatus.INACTIVE, label: "Inactive" },
];
const skills = [
  "installation",
  "packing",
  "handling",
  "clientRelations",
  "teamwork",
];

type Props = {};

export const ModalStaff: FC<Props> = () => {
  const qc = useQueryClient();

  const { formState, closeStaffModal } = useStaffStore();

  const [form] = Form.useForm<CreateStaffParams>();
  const [editDocForm] = Form.useForm();
  const [uploadDocType, setUploadDocType] = useState<DocumentType>(DocumentType.OTHER);
  const [uploadExpiry, setUploadExpiry] = useState<dayjs.Dayjs | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editingFile, setEditingFile] = useState<TFile | null>(null);

  const { data: staff, isLoading: loadingStaff } = useGetStaffById(
    formState.staffId
  );
  const { data: staffRoles, isLoading: loadingStaffRoles } = useGetStaffRoles();

  // Fetch documents for this staff member
  const { data: filesData, isLoading: loadingFiles } = useGetFiles(
    { staffId: staff?.id || "", page: 1, pageSize: 100 },
    !!staff?.id
  );

  const createFileMutation = useCreateFile();
  const deleteFileMutation = useDeleteFile();
  const updateFileMutation = useUpdateFile();

  const handleUpload = async (file: File) => {
    if (!staff?.id) return;
    setUploading(true);
    try {
      await createFileMutation.mutateAsync({
        title: file.name,
        file,
        staffId: staff.id,
        type: uploadDocType,
        expires: uploadExpiry?.toISOString(),
      });
      message.success("Document uploaded successfully");
      qc.invalidateQueries({ queryKey: FILE_KEYS.lists() });
      setUploadExpiry(null);
    } catch (error: any) {
      console.error("File upload error:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload document";
      message.error(Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteFileMutation.mutateAsync(fileId);
      message.success("Document deleted");
      qc.invalidateQueries({ queryKey: FILE_KEYS.lists() });
    } catch (error) {
      message.error("Failed to delete document");
    }
  };

  const handleEditFile = (file: TFile) => {
    setEditingFile(file);
    editDocForm.setFieldsValue({
      type: file.type,
      expires: file.expires ? dayjs(file.expires) : null,
    });
  };

  const handleUpdateFile = async () => {
    if (!editingFile) return;
    try {
      const values = await editDocForm.validateFields();
      await updateFileMutation.mutateAsync({
        id: editingFile.id,
        type: values.type,
        expires: values.expires?.toISOString(),
      });
      message.success("Document updated");
      qc.invalidateQueries({ queryKey: FILE_KEYS.lists() });
      setEditingFile(null);
      editDocForm.resetFields();
    } catch (error) {
      message.error("Failed to update document");
    }
  };

  const getDocumentTypeLabel = (type: DocumentType) => {
    const found = STAFF_DOCUMENT_TYPES.find((t) => t.value === type);
    return found?.label || type;
  };

  useEffect(() => {
    if (!staff || !staff.user) return;

    // Convert staffRoleAssignments to form format
    const roleAssignments: StaffRoleAssignmentInput[] =
      staff.staffRoleAssignments?.map((ra) => ({
        id: ra.id,
        staffRoleId: ra.staffRoleId,
        customHourlyCost: ra.customHourlyCost ?? undefined,
        isPrimary: ra.isPrimary,
      })) ?? [];

    // If no assignments exist but there's a legacy staffRole, create one assignment from it
    if (roleAssignments.length === 0 && staff.staffRole?.id) {
      roleAssignments.push({
        staffRoleId: staff.staffRole.id,
        isPrimary: true,
      });
    }

    form.setFieldsValue({
      username: staff.user.username,
      email: staff.user.email,
      firstName: staff.user.firstName,
      lastName: staff.user.lastName,
      phoneNumber: staff.user.phoneNumber,
      staff: {
        staffRoleId: staff.staffRole?.id,
        employmentType: staff.employmentType ?? EmploymentType.W2,
        staffRoleAssignments: roleAssignments,
        clientRelations: staff.clientRelations,
        installation: staff.installation,
        packing: staff.packing,
        handling: staff.handling,
        teamwork: staff.teamwork,
        lead: staff.lead,
        driver: staff.driver,
        position: staff.position ?? "",
        skills: staff.skills ?? [],
        status: staff.status ?? StaffStatus.ACTIVE,
      },
    });
  }, [staff, form]);

  const mutation = useCreateStaff();

  const updateMutation = useUpdateStaff();

  const onFinish: FormProps<CreateStaffParams>["onFinish"] = (values) => {
    const onSuccess = () => {
      qc.invalidateQueries({
        queryKey: [OPERATION_KEYS.STAFF],
      });
      closeStaffModal();
    };
    if (!staff) {
      mutation.mutate(
        {
          ...values,
          username: values.username.trim(),
          role: Role.STAFF,
          password: "password123",
        },
        {
          onSuccess,
        }
      );
    } else {
      updateMutation.mutate(
        {
          ...values,
          id: staff.user.id as string,
        },
        {
          onSuccess,
        }
      );
    }
  };

  return (
    <Modal
      destroyOnHidden
      open={formState.open}
      onCancel={() => {
        closeStaffModal();
        form.resetFields();
      }}
      footer={null}
      className="w-5xl!"
      title={
        <div className="flex flex-col justify-center items-center gap-1">
          <span className="text-2xl font-semibold">
            {!staff ? "Create New Staff" : `Edit ${staff.user.username}`}
          </span>
          {!staff && (
            <span className="text-sm text-gray-500 ml-2">
              Fill in the details below
            </span>
          )}
        </div>
      }
    >
      <Spin spinning={loadingStaff || loadingStaffRoles}>
        <Form form={form} size="large" layout="vertical" onFinish={onFinish}>
          <Row gutter={16} className="mb-4">
            <Col span={16}>
              <Card title="Staff Information">
                <Form.Item
                  label="Username"
                  name="username"
                  rules={[
                    { required: true, message: "Please input username!" },
                  ]}
                >
                  <Input placeholder="Enter username" />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ required: true, message: "Please input email!" }]}
                >
                  <Input type="email" placeholder="Enter email" />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="First Name"
                      name="firstName"
                      rules={[
                        { required: true, message: "Please enter first name!" },
                      ]}
                    >
                      <Input placeholder="Enter first name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Last Name"
                      name="lastName"
                      rules={[
                        { required: true, message: "Please enter last name!" },
                      ]}
                    >
                      <Input placeholder="Enter last name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Phone"
                  name="phoneNumber"
                  rules={[
                    { required: true, message: "Please input phone number!" },
                  ]}
                >
                  <Input type="phone" placeholder="Enter phone number" />
                </Form.Item>

                <Form.Item
                  label="Employment Type"
                  name={["staff", "employmentType"]}
                  initialValue={EmploymentType.W2}
                >
                  <Segmented
                    options={[
                      { label: "W2 Employee", value: EmploymentType.W2 },
                      { label: "1099 Contractor", value: EmploymentType.CONTRACTOR_1099 },
                    ]}
                  />
                </Form.Item>

                <Form.Item label="Staff Roles">
                  <Form.List name={["staff", "staffRoleAssignments"]}>
                    {(fields, { add, remove }) => (
                      <div className="flex flex-col gap-2">
                        {fields.map(({ key, name, ...restField }) => (
                          <Card
                            key={key}
                            size="small"
                            className="bg-gray-50"
                          >
                            <Row gutter={8} align="middle">
                              <Col span={10}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "staffRoleId"]}
                                  rules={[{ required: true, message: "Select role" }]}
                                  className="mb-0"
                                >
                                  <Select
                                    placeholder="Select role"
                                    loading={loadingStaffRoles}
                                    options={staffRoles?.items.map((role) => ({
                                      label: role.name,
                                      value: role.id,
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={7}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "customHourlyCost"]}
                                  className="mb-0"
                                  tooltip="Custom cost per hour (what we pay staff)"
                                >
                                  <InputNumber
                                    placeholder="Custom Cost/hr"
                                    prefix="$"
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "isPrimary"]}
                                  valuePropName="checked"
                                  className="mb-0"
                                  tooltip="Mark as primary role"
                                >
                                  <Switch
                                    checkedChildren="Primary"
                                    unCheckedChildren=""
                                    size="small"
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={1}>
                                {fields.length > 1 && (
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(name)}
                                    size="small"
                                  />
                                )}
                              </Col>
                            </Row>
                          </Card>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add({ isPrimary: fields.length === 0 })}
                          icon={<PlusOutlined />}
                          className="w-full"
                        >
                          Add Role
                        </Button>
                      </div>
                    )}
                  </Form.List>
                </Form.Item>
                <Form.Item
                  label="Position"
                  name={["staff", "position"]}
                  rules={[{ required: false }]}
                >
                  <Input placeholder="Enter primary position" />
                </Form.Item>

                <Form.Item
                  label="Skills & Certifications"
                  name={["staff", "skills"]}
                  rules={[{ required: false }]}
                  help="Enter skills and certifications separated by commas (e.g., Forklift Certified, Art Handling, Installation)"
                >
                  <Select
                    mode="tags"
                    placeholder="Enter skills and certifications"
                    style={{ width: '100%' }}
                    tokenSeparators={[',']}
                    maxTagCount="responsive"
                  />
                </Form.Item>

                <div className="flex items-center gap-4">
                  <Form.Item
                    label="Is Lead"
                    name={["staff", "lead"]}
                    valuePropName="checked"
                    layout="horizontal"
                    className="mb-0"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label="Is Driver"
                    name={["staff", "driver"]}
                    valuePropName="checked"
                    layout="horizontal"
                    className="mb-0"
                  >
                    <Switch />
                  </Form.Item>
                </div>

                {/* Status dropdown - only show when editing existing staff */}
                {staff && (
                  <Form.Item
                    label="Status"
                    name={["staff", "status"]}
                    className="mt-4"
                  >
                    <Select
                      placeholder="Select status"
                      options={staffStatusOptions}
                    />
                  </Form.Item>
                )}
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Staff Skills">
                {skills.map((skill) => (
                  <div
                    className="grid grid-cols-18 gap-4 items-center"
                    key={skill}
                  >
                    <div className="col-span-8 flex items-center">
                      <span className="font-bold">{capitalize(skill)}</span>
                    </div>
                    <div className="col-span-10 flex items-center">
                      <Form.Item
                        name={["staff", skill]}
                        key={skill}
                        valuePropName="rating"
                        rules={[
                          {
                            required: true,
                            message: "Please rate this skill!",
                          },
                        ]}
                        className="mb-0"
                      >
                        <StarRating interactive />
                      </Form.Item>
                    </div>
                  </div>
                ))}
                <Divider />
                <Form.Item shouldUpdate>
                  {({ getFieldValue }) => {
                    const installation =
                      getFieldValue(["staff", "installation"]) ?? 0;
                    const packing = getFieldValue(["staff", "packing"]) ?? 0;
                    const handling = getFieldValue(["staff", "handling"]) ?? 0;
                    const clientRelations =
                      getFieldValue(["staff", "clientRelations"]) ?? 0;
                    const teamwork = getFieldValue(["staff", "teamwork"]) ?? 0;
                    const rating =
                      (installation +
                        packing +
                        handling +
                        clientRelations +
                        teamwork) /
                      5;
                    return (
                      <div className="grid grid-cols-16 gap-4 items-center">
                        <div className="col-span-6 flex items-center">
                          <span className="font-bold">Overall ({rating})</span>
                        </div>
                        <div className="col-span-10 flex items-center">
                          <Form.Item className="mb-0">
                            <StarRating rating={rating} />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>
              </Card>
            </Col>
          </Row>

          {/* Documents Section - only show when editing existing staff */}
          {staff && (
            <Card title="Staff Documents" className="mb-4">
              <div style={{ marginBottom: 16 }}>
                <Space wrap>
                  <Select
                    style={{ width: 200 }}
                    value={uploadDocType}
                    onChange={setUploadDocType}
                    options={STAFF_DOCUMENT_TYPES}
                    placeholder="Document Type"
                  />
                  <DatePicker
                    placeholder="Expiry Date"
                    value={uploadExpiry}
                    onChange={setUploadExpiry}
                    style={{ width: 150 }}
                  />
                  <Upload
                    beforeUpload={(file) => {
                      handleUpload(file);
                      return false;
                    }}
                    showUploadList={false}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={uploading}
                      type="primary"
                    >
                      Upload
                    </Button>
                  </Upload>
                </Space>
              </div>

              <Spin spinning={loadingFiles}>
                {filesData?.items && filesData.items.length > 0 ? (
                  <Table
                    dataSource={filesData.items}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: "Document",
                        dataIndex: "fileName",
                        key: "fileName",
                        render: (text, record) => (
                          <Space>
                            <FileOutlined />
                            <span style={{ fontSize: 12 }}>{text || record.title}</span>
                          </Space>
                        ),
                      },
                      {
                        title: "Type",
                        dataIndex: "type",
                        key: "type",
                        width: 150,
                        render: (type) => (
                          <Tag style={{ fontSize: 11 }}>{getDocumentTypeLabel(type)}</Tag>
                        ),
                      },
                      {
                        title: "Expires",
                        dataIndex: "expires",
                        key: "expires",
                        width: 110,
                        render: (expires) =>
                          expires ? (
                            <Tag
                              color={
                                dayjs(expires).isBefore(dayjs())
                                  ? "red"
                                  : dayjs(expires).isBefore(dayjs().add(30, "day"))
                                  ? "orange"
                                  : "green"
                              }
                              style={{ fontSize: 11 }}
                            >
                              {dayjs(expires).format("MM/DD/YY")}
                            </Tag>
                          ) : (
                            <span style={{ color: "#999", fontSize: 11 }}>-</span>
                          ),
                      },
                      {
                        title: "",
                        key: "actions",
                        width: 120,
                        render: (_, record) => (
                          <Space size="small">
                            <Button
                              type="text"
                              icon={<EyeOutlined />}
                              size="small"
                              title="View/Download"
                              onClick={() => window.open(record.filePath, "_blank")}
                            />
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              size="small"
                              title="Edit"
                              onClick={() => handleEditFile(record)}
                            />
                            <Popconfirm
                              title="Delete document?"
                              onConfirm={() => handleDeleteFile(record.id)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                title="Delete"
                              />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "#999" }}>
                    No documents uploaded
                  </div>
                )}
              </Spin>
            </Card>
          )}

          <Button
            type="primary"
            htmlType="submit"
            className="w-full"
            size="large"
            loading={mutation.isPending || updateMutation.isPending}
          >
            {!staff ? "Create" : "Edit"} Staff
          </Button>
        </Form>
      </Spin>

      {/* Edit Document Modal */}
      <Modal
        title="Edit Document"
        open={!!editingFile}
        onCancel={() => {
          setEditingFile(null);
          editDocForm.resetFields();
        }}
        onOk={handleUpdateFile}
        okText="Save"
        confirmLoading={updateFileMutation.isPending}
      >
        <Form form={editDocForm} layout="vertical">
          <Form.Item
            name="type"
            label="Document Type"
            rules={[{ required: true, message: "Please select a document type" }]}
          >
            <Select
              options={STAFF_DOCUMENT_TYPES}
              placeholder="Select document type"
            />
          </Form.Item>
          <Form.Item name="expires" label="Expiry Date">
            <DatePicker style={{ width: "100%" }} placeholder="Select expiry date (optional)" />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};
